/**
 * Page-mode slot rendered by `<ZoomPanel>` in place of the join prompt when the
 * reopened encounter already has a Progress Note (PD-1105). Same fixed footprint
 * as `<ZoomPanelPreJoin>`; offers edit/view (read-only when locked) or a new visit.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  IconFileText,
  IconPencil,
  IconEye,
  IconVideoPlus,
  IconMessageCircle,
  IconSparkles,
  IconRefresh,
} from '@tabler/icons-react'
import { CHART_CONTENT_HEIGHT_VAR, CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

export type ExistingNoteNoticeProps = {
  /** Panel width (px) — matches the live rail so the slot lines up. */
  width: number
  /** Patient display name. Falls back to "this visit". */
  patientName?: string
  /** Enables the BeAssistant chat-history button when a pinned conversation exists. */
  patientId?: string
  /** Locked note → primary action is read-only "View note"; otherwise "Edit note". */
  isLocked?: boolean
  /** Open the existing note (editor when unlocked, viewer when locked). */
  onViewEditNote?: () => void
  /** Start a fresh ad-hoc visit for this patient. */
  onStartNewVisit?: () => void
  /** Reload the original AI-generated note content. Rendered only when
      provided (unlocked note + a usable `ScribeDraft` exists + not a manager). */
  onLoadAiDraft?: () => void
  /** Regenerate the note from the saved transcript. Rendered only when
      provided (unlocked note + a `ScribeTranscript` exists + not a manager). */
  onRegenerateNote?: () => void
  /** Which AI action is in flight — disables both buttons and puts the
      spinner/"Working…" treatment on the active one only. */
  aiActionBusy?: 'load' | 'regenerate' | null
}

/** Shared look of every secondary action in the button column. */
const SECONDARY_BUTTON_CLASS =
  'flex items-center justify-center gap-2 rounded-lg border border-white/15 bg-transparent px-5 py-2.5 text-[13px] font-semibold text-slate-200 hover:bg-white/5'

export function ExistingNoteNotice({
  width,
  patientName,
  patientId,
  isLocked,
  onViewEditNote,
  onStartNewVisit,
  onLoadAiDraft,
  onRegenerateNote,
  aiActionBusy,
}: ExistingNoteNoticeProps) {
  const client = useClient()
  const navigate = useNavigate()
  // The caller's most recent BeAssistant conversation pinned to this patient
  // (threads are per-practitioner and expire on their Redis TTL, so absence is
  // normal) — the history button renders only when one exists.
  const [historyConversationId, setHistoryConversationId] = useState<string | null>(null)
  useEffect(() => {
    if (!patientId) return
    let cancelled = false
    void client
      .assistantList({})
      .then((res) => {
        if (cancelled) return
        const latest = res.conversations.find((c) => c.context?.patientId === patientId)
        setHistoryConversationId(latest?.conversationId ?? null)
      })
      .catch((err) => logError('scribe-assistant', 'chat history lookup failed', err))
    return () => {
      cancelled = true
    }
  }, [client, patientId])
  return (
    <aside
      data-testid="zoom-panel-existing-note"
      className="fixed flex flex-col items-center justify-center gap-5 overflow-hidden bg-[#0f172a] px-6 text-center text-slate-200"
      style={{
        top: CHART_CONTENT_TOP_VAR,
        right: 0,
        width: `${width}px`,
        height: CHART_CONTENT_HEIGHT_VAR,
        zIndex: 'var(--z-scribe)' as unknown as number,
        borderLeft: '1px solid #1e293b',
      }}
    >
      <div
        className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-amber-400/30 text-amber-300"
        style={{ background: 'linear-gradient(135deg, #475569, #334155)' }}
      >
        <IconFileText size={34} />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-base font-semibold text-slate-100">Progress Note already exists</span>
        <span className="max-w-[240px] text-[13px] leading-snug text-slate-400">
          A Progress Note has already been created for {patientName ?? 'this visit'}. You don't need
          to rejoin the call.
        </span>
      </div>

      <div className="flex w-full max-w-[240px] flex-col gap-2">
        <button
          type="button"
          onClick={() => onViewEditNote?.()}
          data-testid="zoom-panel-existing-note-open"
          className="flex items-center justify-center gap-2 rounded-lg bg-[#2563eb] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1d4ed8]"
        >
          {isLocked ? <IconEye size={18} /> : <IconPencil size={18} />}
          {isLocked ? 'View note' : 'Edit note'}
        </button>
        {onLoadAiDraft && (
          <button
            type="button"
            onClick={() => onLoadAiDraft()}
            disabled={Boolean(aiActionBusy)}
            aria-busy={aiActionBusy === 'load'}
            data-testid="zoom-panel-existing-note-load-draft"
            className={`${SECONDARY_BUTTON_CLASS} disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {aiActionBusy === 'load' ? (
              <IconRefresh size={18} className="animate-spin" />
            ) : (
              <IconSparkles size={18} />
            )}
            {aiActionBusy === 'load' ? 'Working…' : 'Reload Original Progress Note'}
          </button>
        )}
        {onRegenerateNote && (
          <button
            type="button"
            onClick={() => onRegenerateNote()}
            disabled={Boolean(aiActionBusy)}
            aria-busy={aiActionBusy === 'regenerate'}
            data-testid="zoom-panel-existing-note-regenerate"
            className={`${SECONDARY_BUTTON_CLASS} disabled:cursor-not-allowed disabled:opacity-60`}
          >
            <IconRefresh
              size={18}
              className={aiActionBusy === 'regenerate' ? 'animate-spin' : undefined}
            />
            {aiActionBusy === 'regenerate' ? 'Working…' : 'Regenerate note'}
          </button>
        )}
        <button
          type="button"
          onClick={() => onStartNewVisit?.()}
          data-testid="zoom-panel-existing-note-new-visit"
          className={SECONDARY_BUTTON_CLASS}
        >
          <IconVideoPlus size={18} />
          Start a new visit
        </button>
        {historyConversationId && (
          <button
            type="button"
            onClick={() => void navigate(`/assistant?conversation=${historyConversationId}`)}
            data-testid="zoom-panel-existing-note-chat-history"
            className={SECONDARY_BUTTON_CLASS}
          >
            <IconMessageCircle size={18} />
            BeAssistant chat history
          </button>
        )}
      </div>
    </aside>
  )
}
